import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useMatrix } from "~/context/MatrixContext";
import {
  DEFAULT_DND_ACCOUNT_DATA,
  subscribeDnd,
  writeDnd,
  type Client,
  type DndAccountData,
  type DndScheduleWindow,
} from "~/lib/matrix";

export type DndSource = "manual" | "schedule" | null;

export interface DndState {
  active: boolean;
  source: DndSource;
  until: Date | null;
}

export interface UseDndResult extends DndState {
  data: DndAccountData;
  setManualUntil: (until: Date | null) => void;
  setSchedule: (schedule: DndAccountData["schedule"]) => void;
  setOff: () => void;
}

export function useDnd(clientOverride?: Client | null): UseDndResult {
  const matrix = useMatrix();
  const client = clientOverride === undefined ? matrix.client : clientOverride;
  const [data, setData] = useState<DndAccountData>(DEFAULT_DND_ACCOUNT_DATA);
  const [now, setNow] = useState(() => Date.now());
  const writeVersionRef = useRef(0);

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (!client) {
      setData(DEFAULT_DND_ACCOUNT_DATA);
      return;
    }
    return subscribeDnd(client, (next) => {
      writeVersionRef.current += 1;
      setData(next);
    });
  }, [client]);

  const setManualUntil = useCallback(
    (until: Date | null) => {
      if (!client) return;
      const next: DndAccountData = {
        ...data,
        manual: { until: until ? until.getTime() : null },
      };
      const version = writeVersionRef.current + 1;
      writeVersionRef.current = version;
      const previous = data;
      setData(next);
      writeDnd(client, next).catch((err) => {
        console.warn("writeDnd manual failed", err);
        if (writeVersionRef.current === version) setData(previous);
      });
    },
    [client, data],
  );

  const setSchedule = useCallback(
    (schedule: DndAccountData["schedule"]) => {
      if (!client) return;
      const next: DndAccountData = { ...data, schedule };
      const version = writeVersionRef.current + 1;
      writeVersionRef.current = version;
      const previous = data;
      setData(next);
      writeDnd(client, next).catch((err) => {
        console.warn("writeDnd schedule failed", err);
        if (writeVersionRef.current === version) setData(previous);
      });
    },
    [client, data],
  );

  const setOff = useCallback(() => {
    if (!client) return;
    const next: DndAccountData = {
      manual: { until: null },
      schedule: { ...data.schedule, enabled: false },
    };
    const version = writeVersionRef.current + 1;
    writeVersionRef.current = version;
    const previous = data;
    setData(next);
    writeDnd(client, next).catch((err) => {
      console.warn("writeDnd off failed", err);
      if (writeVersionRef.current === version) setData(previous);
    });
  }, [client, data]);

  const evaluated = evaluateDnd(data, now);
  const until = useMemo(() => {
    if (evaluated.source !== "manual" || !data.manual.until) return null;
    return new Date(data.manual.until);
  }, [data.manual.until, evaluated.source]);
  return { ...evaluated, until, data, setManualUntil, setSchedule, setOff };
}

export function evaluateDnd(data: DndAccountData, nowMs: number = Date.now()): DndState {
  const manualUntil = data.manual.until;
  if (manualUntil && manualUntil > nowMs) {
    return { active: true, source: "manual", until: new Date(manualUntil) };
  }
  if (
    data.schedule.enabled &&
    data.schedule.windows.some((window) => windowActive(window, nowMs))
  ) {
    return { active: true, source: "schedule", until: null };
  }
  return { active: false, source: null, until: null };
}

function windowActive(window: DndScheduleWindow, nowMs: number): boolean {
  const now = new Date(nowMs);
  const currentDay = now.getDay();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const start = minutesFor(window.start);
  const end = minutesFor(window.end);
  if (start === end) return false;
  if (start < end) {
    return (
      window.days.includes(currentDay as DndScheduleWindow["days"][number]) &&
      currentMinutes >= start &&
      currentMinutes < end
    );
  }
  const previousDay = ((currentDay + 6) % 7) as DndScheduleWindow["days"][number];
  const startsToday =
    window.days.includes(currentDay as DndScheduleWindow["days"][number]) &&
    currentMinutes >= start;
  const continuesToday = window.days.includes(previousDay) && currentMinutes < end;
  return startsToday || continuesToday;
}

function minutesFor(value: string): number {
  const [hourRaw, minuteRaw] = value.split(":");
  const hour = Number(hourRaw ?? 0);
  const minute = Number(minuteRaw ?? 0);
  return hour * 60 + minute;
}
